import { useState, useEffect, useCallback, useRef } from 'react';
import { AppNode, NodeStatus } from '@/types';

export interface UseNodeInteractionOptions {
  data: AppNode;
}

export function useNodeInteraction(options: UseNodeInteractionOptions) {
  const { data } = options;
  const isWorking = data.status === NodeStatus.WORKING;
  const isIdle = data.status === NodeStatus.IDLE;

  const [isHovered, setIsHovered] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isCtrlHeld, setIsCtrlHeld] = useState(false);
  const [activeFeature, setActiveFeature] = useState<{ id: string; label: string } | null>(
    isIdle && data.data.activeFeature ? { id: data.data.activeFeature, label: data.data.activeFeature } : null
  );
  const [gridSplit, setGridSplit] = useState<{ rows: number; cols: number } | null>(null);
  const [showPanoramaPanel, setShowPanoramaPanel] = useState(false);
  const [chargeComplete, setChargeComplete] = useState(false);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());

  const prevWorkingRef = useRef(false);

  useEffect(() => {
    if (!isWorking && prevWorkingRef.current) {
      setChargeComplete(true);
      const t = setTimeout(() => {
        setChargeComplete(false);
      }, 600);
      prevWorkingRef.current = isWorking;
      return () => clearTimeout(t);
    }
    prevWorkingRef.current = isWorking;
  }, [isWorking]);

  useEffect(() => {
    if (isIdle && data.data.activeFeature) {
      setActiveFeature({ id: data.data.activeFeature, label: data.data.activeFeature });
    }
  }, [isIdle, data.data.activeFeature]);

  useEffect(() => {
    if (isWorking && activeFeature) {
      setActiveFeature(null);
    }
  }, [isWorking, activeFeature]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) setIsCtrlHeld(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) setIsCtrlHeld(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleFeatureSelect = useCallback((id: string, label: string) => {
    if (id === 'split') {
      const match = label.match(/(\d+)×(\d+)/);
      if (match) {
        // P0(v9): label 格式为 cols×rows（横向×纵向），如 "3×1宫格切分" = 横3纵1
        // match[1] = cols（横向），match[2] = rows（纵向）
        setGridSplit({ rows: parseInt(match[2]), cols: parseInt(match[1]) });
        setSelectedCells(new Set());
      }
    } else {
      setActiveFeature({ id, label });
    }
  }, []);

  const handlePanoramaClick = useCallback(() => {
    setShowPanoramaPanel(true);
  }, []);

  const handleGridSplitClose = useCallback(() => {
    setGridSplit(null);
    setSelectedCells(new Set());
  }, []);

  const handleFeatureClose = useCallback(() => {
    setActiveFeature(null);
  }, []);

  const handlePanoramaClose = useCallback(() => {
    setShowPanoramaPanel(false);
  }, []);

  return {
    isHovered,
    setIsHovered,
    isInputFocused,
    setIsInputFocused,
    isCtrlHeld,
    activeFeature,
    gridSplit,
    showPanoramaPanel,
    chargeComplete,
    selectedCells,
    setSelectedCells,
    handleFeatureSelect,
    handlePanoramaClick,
    handleGridSplitClose,
    handleFeatureClose,
    handlePanoramaClose,
  };
}
